import * as THREE from 'three';
import { getCoreDieAppearance } from './coreDieAppearance';

// Cosmetic only: both die scenes retain their existing cube and roll physics.
export const createCoreDieScene = (scene, die, size) => {
  const armor = new THREE.Group();
  die.add(armor);
  const atmosphere = new THREE.Group();
  scene.add(atmosphere);
  armor.visible = atmosphere.visible = false;

  const metal = new THREE.MeshPhysicalMaterial({
    color: 0x080610,
    metalness: 0.94,
    roughness: 0.27,
    clearcoat: 1,
  });
  const rim = new THREE.MeshBasicMaterial({
    color: 0xbd69ff,
    wireframe: true,
    transparent: true,
    opacity: 0.6,
    toneMapped: false,
  });
  const corners = new THREE.InstancedMesh(
    new THREE.OctahedronGeometry(size * 0.115),
    metal,
    8
  );
  const cornerRims = new THREE.InstancedMesh(corners.geometry, rim, 8);
  cornerRims.instanceMatrix = corners.instanceMatrix;
  const shards = new THREE.InstancedMesh(
    new THREE.OctahedronGeometry(size * 0.09),
    metal,
    10
  );
  const shardRims = new THREE.InstancedMesh(shards.geometry, rim, 10);
  shardRims.instanceMatrix = shards.instanceMatrix;
  [corners, cornerRims, shards, shardRims].forEach((mesh) => {
    mesh.frustumCulled = false;
    armor.add(mesh);
  });
  const ringMaterial = new THREE.MeshBasicMaterial({
    color: 0xbd69ff,
    transparent: true,
    opacity: 0.5,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
  });
  const seals = [0, 1].map((index) => {
    const seal = new THREE.Mesh(
      new THREE.TorusGeometry(size * 0.78, size * 0.012, 4, 4),
      ringMaterial
    );
    seal.rotation.set((index * Math.PI) / 2, 0, Math.PI / 4);
    armor.add(seal);
    return seal;
  });
  const arcs = [0, 1, 2].map((index) => {
    const arc = new THREE.Mesh(
      new THREE.TorusGeometry(
        size * (0.78 + index * 0.13),
        size * 0.009,
        4,
        40,
        Math.PI * (1.15 + index * 0.2)
      ),
      ringMaterial
    );
    armor.add(arc);
    return arc;
  });

  const smokeMaterial = new THREE.ShaderMaterial({
    uniforms: {
      time: { value: 0 },
      power: { value: 0 },
      strength: { value: 1 },
      tint: { value: new THREE.Color(0xbd69ff) },
    },
    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      varying vec2 vUv;
      uniform float time;
      uniform float power;
      uniform float strength;
      uniform vec3 tint;
      void main() {
        vec2 p = (vUv - 0.5) * 2.0;
        float radius = length(p);
        float angle = atan(p.y, p.x);
        float curl = sin(angle * 7.0 + time * 1.1 + radius * 12.0)
          * sin(angle * 11.0 - time * 0.7 - radius * 8.0);
        float edge = 0.78 + curl * (0.055 + power * 0.012);
        float fog = smoothstep(0.29, 0.5, radius)
          * (1.0 - smoothstep(edge - 0.2, edge + 0.14, radius));
        float corona = exp(-pow((radius - edge) * 36.0, 2.0));
        float tendrils = pow(0.5 + 0.5 * sin(angle * 13.0 + curl * 3.0 + time), 5.0);
        vec3 darkness = mix(vec3(0.004, 0.002, 0.009), tint * 0.055, tendrils);
        vec3 color = darkness + tint * corona * (0.22 + power * 0.04);
        float alpha = fog * (0.62 + power * 0.035) + corona * 0.24;
        gl_FragColor = vec4(color, clamp(alpha * strength, 0.0, 0.88));
      }
    `,
    transparent: true,
    depthWrite: false,
    toneMapped: false,
  });
  const smoke = new THREE.Mesh(
    new THREE.PlaneGeometry(size * 3.25, size * 3.25),
    smokeMaterial
  );
  atmosphere.add(smoke);
  const motePositions = new Float32Array(28 * 3);
  const moteGeometry = new THREE.BufferGeometry();
  moteGeometry.setAttribute(
    'position',
    new THREE.BufferAttribute(motePositions, 3)
  );
  const moteMaterial = new THREE.PointsMaterial({
    color: 0xbd69ff,
    size: size * 0.035,
    transparent: true,
    opacity: 0.55,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    toneMapped: false,
  });
  const motes = new THREE.Points(moteGeometry, moteMaterial);
  motes.frustumCulled = false;
  atmosphere.add(motes);
  const transform = new THREE.Object3D();
  const worldScale = new THREE.Vector3();
  let lastPhase = null;

  return {
    update({
      phase,
      elapsed,
      camera,
      moving = false,
      reduceMotion = false,
      visible = true,
    }) {
      armor.visible = atmosphere.visible = visible;
      if (!visible) return;
      const skin = getCoreDieAppearance(phase);
      const level = skin.level;
      const time = reduceMotion ? 0 : elapsed;
      if (lastPhase !== skin.phase) {
        lastPhase = skin.phase;
        metal.color.set(skin.body);
        metal.emissive.set(skin.emissive);
        metal.emissiveIntensity = 0.45;
        rim.color.set(skin.accent);
        ringMaterial.color.set(skin.accent);
        moteMaterial.color.set(skin.light);
        smokeMaterial.uniforms.tint.value.set(skin.accent);
        smokeMaterial.uniforms.power.value = level;
      }
      die.getWorldPosition(atmosphere.position);
      die.getWorldScale(worldScale);
      atmosphere.scale.copy(worldScale);
      smoke.quaternion.copy(camera.quaternion);
      smokeMaterial.uniforms.time.value = time;
      smokeMaterial.uniforms.strength.value = moving ? 1 : 0.72;
      smoke.scale.setScalar(
        1 + (reduceMotion ? 0 : Math.sin(time * (1.8 + level * 0.3)) * 0.045)
      );

      for (let index = 0; index < 8; index += 1) {
        const separation =
          level === 0
            ? 0
            : 0.035 + level * 0.008 + Math.sin(time * 1.6 + index) * 0.012;
        const offset = size * (0.49 + separation);
        transform.position.set(
          index & 1 ? offset : -offset,
          index & 2 ? offset : -offset,
          index & 4 ? offset : -offset
        );
        transform.rotation.set(
          index * 0.4,
          index * 0.7,
          level > 0 ? time * 0.2 : Math.PI / 4
        );
        transform.scale.set(1, level >= 3 ? 1.6 : 1, 1);
        transform.updateMatrix();
        corners.setMatrixAt(index, transform.matrix);
      }
      corners.instanceMatrix.needsUpdate = true;
      seals.forEach((seal) => {
        seal.visible = level === 0;
      });
      arcs.forEach((arc, index) => {
        arc.visible = level > 0 && (index < 2 || level >= 3);
        arc.rotation.set(
          0.6 + index * 1.1,
          time * (0.25 + level * 0.1) * (index % 2 ? -1 : 1) + index,
          time * 0.15 + index * 0.8
        );
      });
      shards.visible = shardRims.visible = level > 0;
      for (let index = 0; index < 10; index += 1) {
        const angle = (index * Math.PI) / 5 + time * (0.2 + level * 0.075);
        const crown = level === 3;
        const radius =
          size * (crown ? 0.72 : 0.84 + Math.sin(time + index) * 0.07);
        transform.position.set(
          Math.cos(angle) * radius,
          size *
            (crown
              ? 0.48
              : Math.sin(angle * 2 + index) * (0.28 + level * 0.045)),
          Math.sin(angle) * radius
        );
        transform.rotation.set(
          crown ? 0 : angle,
          angle,
          crown ? 0 : time * 0.45 + index
        );
        transform.scale.set(0.65, crown ? 2.4 : 1.1 + level * 0.3, 0.65);
        transform.updateMatrix();
        shards.setMatrixAt(index, transform.matrix);
      }
      shards.instanceMatrix.needsUpdate = true;
      motes.visible = !reduceMotion;
      moteMaterial.opacity = (moving ? 0.65 : 0.35) + level * 0.035;
      for (let index = 0; index < 28; index += 1) {
        const angle = index * 2.39996 - time * (0.2 + level * 0.08);
        const rise = (index / 28 + time * (0.11 + level * 0.025)) % 1;
        const radius = size * (0.7 + rise * 0.55);
        motePositions[index * 3] = Math.cos(angle) * radius;
        motePositions[index * 3 + 1] = size * (rise * 1.7 - 0.7);
        motePositions[index * 3 + 2] = Math.sin(angle) * radius;
      }
      moteGeometry.attributes.position.needsUpdate = true;
    },
    dispose() {
      const geometries = new Set();
      const materials = new Set();
      [armor, atmosphere].forEach((group) => {
        group.traverse((object) => {
          if (object.isInstancedMesh) object.dispose();
          if (object.geometry) geometries.add(object.geometry);
          if (object.material) materials.add(object.material);
        });
        group.removeFromParent();
      });
      geometries.forEach((geometry) => geometry.dispose());
      materials.forEach((material) => material.dispose());
    },
  };
};
